import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ClearIcon from "@mui/icons-material/Clear";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import BadgeIcon from "@mui/icons-material/Badge";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import PersonIcon from "@mui/icons-material/Person";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import { toast } from "react-toastify";

// Styles
import "./BeneficiarySearch.css";

// API
import { eopdAPI } from "../../../services/e-opd.api";

// ABDM modals
import QRCodeScanner from "../../aggregator/pages/QRCodeScanner";
import { AbhaSendOtp } from "../../aggregator/pages/AbhaSendOtp";
import { AbhaVerifyOtp } from "../../aggregator/pages/AbhaVerifyOtp";
import Modalqr from "../../aggregator/pages/Modalqr";
import AbdmSection from "../../aggregator/pages/AbdmSection/AbdmSection";

/* ------------------------------------------------------------------ */
/* Design tokens — Visit Summary / OPD steps se match karte hain       */
/* ------------------------------------------------------------------ */
const T = {
  ink: "#111C33",
  muted: "#4A5670",
  label: "#2C3A57",
  blue: "#1E88E5",
  blueDark: "#1565C0",
  blueSoft: "#E8F3FE",
  amber: "#9A5B00",
  amberSoft: "#FFF3E0",
  border: "#D8DFEA",
  hairline: "#E3E8F0",
  surface: "#F7F9FC",
};

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

const SEARCH_TYPES = [
  { value: "abha", label: "ABHA ID", icon: <AssignmentIndIcon />, placeholder: "Enter ABHA ID" },
  { value: "patient", label: "Patient ID", icon: <BadgeIcon />, placeholder: "Enter Patient ID" },
  { value: "mobile", label: "Mobile Number", icon: <SmartphoneIcon />, placeholder: "Enter Mobile Number" },
  { value: "name", label: "Name", icon: <PersonIcon />, placeholder: "Enter Name" },
];

const COLUMNS = [
  { key: "id", label: "Patient ID", width: 110 },
  { key: "name", label: "Name", width: 200 },
  { key: "age", label: "Age", width: 70 },
  { key: "gender", label: "Gender", width: 90 },
  { key: "abha", label: "ABHA ID", width: 170 },
  { key: "mobile", label: "Mobile Number", width: 140 },
  { key: "visit", label: "Previous Visit", width: 130 },
  { key: "action", label: "Action", width: 80, align: "center" },
];

/** Muted "N/A" taaki bhara hua data alag dikhe. */
function Value({ children, mono = false }) {
  const empty = children === null || children === undefined || children === "";
  return (
    <Typography
      component="span"
      sx={{
        fontSize: 13,
        fontWeight: empty ? 400 : 500,
        color: empty ? "#8A94A6" : "#2E3A52",
        fontFamily: mono && !empty ? MONO : "inherit",
        whiteSpace: "nowrap",
      }}
    >
      {empty ? "N/A" : children}
    </Typography>
  );
}

export default function BeneficiarySearch() {
  const navigate = useNavigate();

  // Search state
  const [searchType, setSearchType] = useState("abha"); // abha, patient, mobile, name
  const [searchValue, setSearchValue] = useState("");

  // Pagination & List state
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // ABDM Registration modal states
  const [addbenificeryModal, setAddbenificeryModal] = useState(false);
  const [showScannerModal, setshowScannerModal] = useState(false);
  const [isqrenabled, setisqrenabled] = useState(false);
  const [showAbhaSendOtp, setshowAbhaSendOtp] = useState(false);
  const [showAbhaVerifyOtp, setshowAbhaVerifyOtp] = useState(false);
  const [AbhaTxnId, setAbhaTxnId] = useState("");

  const handleSearchTypeChange = (type) => {
    if (!type || type === searchType) return;
    setSearchType(type);
    setSearchValue(type === "patient" ? "P-" : "");
  };

  const activeType = SEARCH_TYPES.find((t) => t.value === searchType) || SEARCH_TYPES[0];

  // Live validator and formatter for inputs
  const handleInputChange = (e) => {
    let val = e.target.value;

    if (searchType === "abha") {
      // 1. ABHA ID: format "91-1234-5678-9012"
      // Only numbers allowed, max 14 digits
      const digits = val.replace(/\D/g, "").substring(0, 14);

      // Auto-insert hyphens: XX-XXXX-XXXX-XXXX
      let formatted = "";
      if (digits.length > 0) {
        formatted += digits.substring(0, 2);
      }
      if (digits.length > 2) {
        formatted += "-" + digits.substring(2, 6);
      }
      if (digits.length > 6) {
        formatted += "-" + digits.substring(6, 10);
      }
      if (digits.length > 10) {
        formatted += "-" + digits.substring(10, 14);
      }
      setSearchValue(formatted);
    } else if (searchType === "patient") {
      // 2. Patient ID: "P-" prefix always forced, only numbers allowed after it
      if (!val.startsWith("P-")) {
        val = "P-";
      }
      const numbers = val.substring(2).replace(/\D/g, "");
      setSearchValue("P-" + numbers);
    } else if (searchType === "mobile") {
      // 3. Mobile Number: only numbers, max 10 digits
      const digits = val.replace(/\D/g, "").substring(0, 10);
      setSearchValue(digits);
    } else if (searchType === "name") {
      // 4. Name: only alphabets and spaces, max 50 characters
      const alphabets = val.replace(/[^a-zA-Z\s]/g, "").substring(0, 50);
      setSearchValue(alphabets);
    }
  };

  const clearSearchValue = () => setSearchValue(searchType === "patient" ? "P-" : "");

  const hasSearchValue = Boolean(searchValue) && searchValue !== "P-";

  // Fetch logic
  const fetchPatients = async (targetPage = page) => {
    try {
      setLoading(true);
      const params = {
        page: targetPage,
        page_size: pageSize,
      };

      // Add filter params if searchValue is not empty/prefix-only
      if (searchValue) {
        if (searchType === "abha") {
          // Send to API in format XX-XXXX-XXXX-XXXX
          params.abha_number = searchValue;
        } else if (searchType === "patient") {
          // Send only numeric part
          const numPart = searchValue.substring(2);
          if (numPart) {
            params.patient_id = numPart;
          }
        } else if (searchType === "mobile") {
          if (searchValue.length === 10) {
            params.mobile_number = searchValue;
          } else {
            toast.error("Please enter a 10-digit mobile number");
            setLoading(false);
            return;
          }
        } else if (searchType === "name") {
          params.name = searchValue;
        }
      }

      const response = await eopdAPI.getPatients(params);
      if (response && response.success) {
        setPatients(response.patients || []);
        setTotal(response.total || 0);
        setPage(response.page || targetPage);
        setPageSize(response.pageSize || pageSize);
        setTotalPages(response.totalPages || 1);
      } else {
        toast.error(response?.message || "Failed to fetch patients");
      }
    } catch (error) {
      console.error("Error fetching patients:", error);
      toast.error("Error fetching patients list");
    } finally {
      setLoading(false);
    }
  };

  // Load initial patient list on mount
  useEffect(() => {
    fetchPatients(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    fetchPatients(1);
  };

  const handleReset = () => {
    setSearchValue(searchType === "patient" ? "P-" : "");
    setLoading(true);
    // searchValue state async hai, isliye reset ke baad plain list mangwate hain
    eopdAPI
      .getPatients({ page: 1, page_size: pageSize })
      .then((response) => {
        if (response && response.success) {
          setPatients(response.patients || []);
          setTotal(response.total || 0);
          setPage(response.page || 1);
          setTotalPages(response.totalPages || 1);
        }
      })
      .catch((err) => {
        console.error("Error resetting patients:", err);
        toast.error("Error fetching patients list");
      })
      .finally(() => setLoading(false));
  };

  /**
   * Visit history page par le jata hai.
   * patient id URL me nahi bhejte — sirf router state me, taaki page
   * sirf isi icon se khule, direct URL se nahi.
   */
  const openVisitHistory = (row) => {
    navigate("/e-opd/visit-history", {
      state: {
        patientId: row.id,
        patient: {
          id: row.id,
          name: row.full_name,
          age: row.age,
          gender_name: row.gender_name,
          mobile_no: row.mobile_no,
          abha_number: row.abha_number,
        },
      },
    });
  };

  const handlePageChange = (_event, newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      fetchPatients(newPage);
    }
  };

  // Modal helper handlers
  const openAddBeneficiaryModal = () => {
    setAddbenificeryModal(true);
  };

  const openSendOtpModal = () => {
    setshowAbhaSendOtp(true);
    setAddbenificeryModal(false);
  };

  const closeSendOtpModal = () => {
    setshowAbhaSendOtp(false);
    setshowAbhaVerifyOtp(false);
  };

  const openScannerModal = () => {
    setisqrenabled(true);
    setshowScannerModal(true);
    setAddbenificeryModal(false);
  };

  const closeScannerModal = () => {
    setshowScannerModal(false);
    setisqrenabled(false);
  };

  const closeVerifyOtpModal = () => setshowAbhaVerifyOtp(false);

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <Box className="eopd-beneficiary-container">
      {/* -------- Page header -------- */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "flex-end" }}
        gap={2}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <img
              src="/images/e-opd-logo.png"
              alt="e-OPD Logo"
              style={{ height: 42, objectFit: "contain" }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: T.ink, lineHeight: 1.2 }}>
            Search Beneficiary
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: T.muted, mt: 0.5 }}>
            Find an existing patient, or register a new one with ABHA.
          </Typography>
        </Box>

        <Button
          variant="contained"
          disableElevation
          startIcon={<AddIcon />}
          onClick={openAddBeneficiaryModal}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "8px",
            px: 2.5,
            flexShrink: 0,
            backgroundColor: T.blue,
            "&:hover": { backgroundColor: T.blueDark },
          }}
        >
          Add Beneficiary
        </Button>
      </Stack>

      {/* -------- Search panel -------- */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2.5,
          borderColor: T.border,
          backgroundColor: "#fff",
          overflow: "hidden",
          mb: 2.5,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{ px: 2.25, py: 1.5, backgroundColor: T.surface, borderBottom: `1px solid ${T.border}` }}
        >
          <Box sx={{ display: "flex", color: T.blue, "& svg": { fontSize: 18 } }}>
            <PersonSearchOutlinedIcon />
          </Box>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".06em",
              textTransform: "uppercase",
              color: T.ink,
            }}
          >
            Search By
          </Typography>
        </Stack>

        <Box sx={{ p: { xs: 2, md: 2.5 } }}>
          {/* Segmented control */}
          <ToggleButtonGroup
            exclusive
            value={searchType}
            onChange={(_e, value) => handleSearchTypeChange(value)}
            sx={{
              flexWrap: "wrap",
              gap: 1,
              mb: 2.5,
              "& .MuiToggleButtonGroup-grouped": {
                border: `1px solid ${T.border} !important`,
                borderRadius: "8px !important",
                textTransform: "none",
                fontSize: 13.5,
                fontWeight: 600,
                color: T.label,
                px: 2,
                py: 0.9,
                gap: 0.75,
                "& svg": { fontSize: 18 },
                "&:hover": { backgroundColor: T.surface, borderColor: "#C2CDDE !important" },
                "&.Mui-selected": {
                  backgroundColor: T.blue,
                  color: "#fff",
                  borderColor: `${T.blue} !important`,
                  "&:hover": { backgroundColor: T.blueDark },
                },
              },
            }}
          >
            {SEARCH_TYPES.map((t) => (
              <ToggleButton key={t.value} value={t.value} aria-label={t.label}>
                {t.icon}
                {t.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* Input + actions */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ maxWidth: 720 }}>
            <TextField
              size="small"
              fullWidth
              placeholder={activeType.placeholder}
              value={searchValue}
              onChange={handleInputChange}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: T.muted }} />
                    </InputAdornment>
                  ),
                  endAdornment: hasSearchValue ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearSearchValue} edge="end" aria-label="Clear">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: 14,
                  backgroundColor: "#fff",
                  "& fieldset": { borderColor: T.border },
                  "&:hover fieldset": { borderColor: "#C2CDDE" },
                  "&.Mui-focused fieldset": { borderColor: T.blue, borderWidth: 1.5 },
                },
              }}
            />
            <Stack direction="row" spacing={1.5} flexShrink={0}>
              <Button
                variant="contained"
                disableElevation
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  px: 3,
                  backgroundColor: T.blue,
                  "&:hover": { backgroundColor: T.blueDark },
                }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={loading || !hasSearchValue}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  px: 2.5,
                  borderColor: T.border,
                  color: T.label,
                  "&:hover": { borderColor: "#C2CDDE", backgroundColor: T.surface },
                }}
              >
                Reset
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      {/* -------- Beneficiary list -------- */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2.5,
          borderColor: T.border,
          backgroundColor: "#fff",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          sx={{ px: 2.25, py: 1.5, backgroundColor: T.surface, borderBottom: `1px solid ${T.border}` }}
        >
          <Box sx={{ display: "flex", color: T.blue, "& svg": { fontSize: 18 } }}>
            <GroupOutlinedIcon />
          </Box>
          <Typography
            sx={{
              flex: 1,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".06em",
              textTransform: "uppercase",
              color: T.ink,
            }}
          >
            Beneficiary List
          </Typography>
          {total > 0 && (
            <Chip
              label={total}
              size="small"
              sx={{
                height: 20,
                minWidth: 24,
                fontSize: 11,
                fontWeight: 700,
                color: T.blue,
                backgroundColor: T.blueSoft,
              }}
            />
          )}
        </Stack>

        <TableContainer
          sx={{
            "&::-webkit-scrollbar": { height: 6 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#C7D0DE", borderRadius: 3 },
          }}
        >
          <Table
            sx={{
              minWidth: 980,
              /* Uniform grid — har cell par same border */
              "& th, & td": {
                borderBottom: `1px solid ${T.border}`,
                borderRight: `1px solid ${T.border}`,
                py: 1.25,
                px: 2,
              },
              "& th:last-of-type, & td:last-of-type": { borderRight: 0 },
              "& tbody tr:last-of-type td": { borderBottom: 0 },
            }}
          >
            <TableHead>
              <TableRow>
                {COLUMNS.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.align || "left"}
                    sx={{
                      width: c.width,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: ".05em",
                      textTransform: "uppercase",
                      color: T.label,
                      backgroundColor: T.surface,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: pageSize }).map((_, i) => (
                  <TableRow key={`skeleton-${i}`}>
                    {COLUMNS.map((c) => (
                      <TableCell key={c.key} align={c.align || "left"}>
                        <Skeleton variant="text" width={c.key === "name" ? "70%" : "55%"} height={20} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : patients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} sx={{ borderRight: 0 }}>
                    <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
                      <PersonSearchOutlinedIcon sx={{ fontSize: 34, color: "#B4BCCB" }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.label }}>
                        No beneficiaries found
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: T.muted, textAlign: "center" }}>
                        {hasSearchValue
                          ? `No match for this ${activeType.label.toLowerCase()}. Try another search or add a new beneficiary.`
                          : "Register a beneficiary to get started."}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                patients.map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      "&:nth-of-type(even)": { backgroundColor: "#FBFCFE" },
                      "&:hover": { backgroundColor: T.blueSoft },
                      transition: "background-color .15s",
                    }}
                  >
                    <TableCell>
                      <Typography
                        sx={{ fontSize: 13, fontWeight: 700, color: T.ink, fontFamily: MONO }}
                      >
                        P-{row.id}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: T.ink }}>
                        {row.full_name || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Value>{row.age}</Value>
                    </TableCell>
                    <TableCell>
                      <Value>{row.gender_name}</Value>
                    </TableCell>
                    <TableCell>
                      <Value mono>{row.abha_number}</Value>
                    </TableCell>
                    <TableCell>
                      <Value mono>{row.mobile_no}</Value>
                    </TableCell>

                    <TableCell>
                      {row.previous_visit_date ? (
                        <Value>{row.previous_visit_date}</Value>
                      ) : (
                        <Chip
                          label="First visit"
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 700,
                            color: T.amber,
                            backgroundColor: T.amberSoft,
                          }}
                        />
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title="View patient's visit history" arrow>
                        <IconButton
                          size="small"
                          onClick={() => openVisitHistory(row)}
                          aria-label={`View visit history of ${row.full_name || `P-${row.id}`}`}
                          sx={{
                            color: T.blue,
                            "&:hover": { backgroundColor: T.blueSoft },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer: range + pagination */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1.5}
          sx={{ px: 2.25, py: 1.5, borderTop: `1px solid ${T.border}`, backgroundColor: T.surface }}
        >
          <Typography sx={{ fontSize: 12.5, color: T.muted }}>
            {loading ? (
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={12} thickness={5} /> Loading…
              </Box>
            ) : total === 0 ? (
              "No results"
            ) : (
              <>
                Showing <b style={{ color: T.ink }}>{rangeStart}–{rangeEnd}</b> of{" "}
                <b style={{ color: T.ink }}>{total}</b> beneficiaries
              </>
            )}
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            disabled={loading}
            shape="rounded"
            siblingCount={0}
            boundaryCount={1}
            sx={{
              "& .MuiPaginationItem-root": {
                fontSize: 13,
                fontWeight: 600,
                color: T.label,
                borderRadius: "8px",
                minWidth: 32,
                height: 32,
              },
              "& .Mui-selected": {
                backgroundColor: `${T.blue} !important`,
                color: "#fff",
                "&:hover": { backgroundColor: `${T.blueDark} !important` },
              },
            }}
          />
        </Stack>
      </Paper>

      {/* ABDM Modals integration */}
      {addbenificeryModal && (
        <AbdmSection
          addbenificeryModal={addbenificeryModal}
          setAddbenificeryModal={setAddbenificeryModal}
          openScannerModal={openScannerModal}
          openSendOtpModal={openSendOtpModal}
          redirectUrl="/e-opd/registration"
        />
      )}

      <Modalqr show={showScannerModal} handleClose={closeScannerModal}>
        {<QRCodeScanner isqrenabled={isqrenabled} setisqrenabled={setisqrenabled} />}
      </Modalqr>

      <AbhaSendOtp
        show={showAbhaSendOtp}
        handleClose={closeSendOtpModal}
        setshowAbhaVerifyOtp={setshowAbhaVerifyOtp}
        setAbhaTxnId={setAbhaTxnId}
      />

      <AbhaVerifyOtp
        show={showAbhaVerifyOtp}
        handleClose={closeVerifyOtpModal}
        AbhaTxnId={AbhaTxnId}
        setshowAbhaVerifyOtp={setshowAbhaVerifyOtp}
        redirectUrl="/e-opd/registration"
      />
    </Box>
  );
}
